import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink
} from '@mui/material';
import {
  School as TrainingIcon,
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  Timer as TimerIcon,
  Category as CategoryIcon,
  Topic as SubcategoryIcon,
  MenuBook as CourseIcon,
  ViewList as OutlineIcon,
  Quiz as ExamOutlineIcon,
  PlayArrow as LaunchIcon,
  LibraryBooks as MaterialsLibraryIcon,
  Visibility as PreviewIcon,
  OpenInNew as OpenIcon,
  Link as LinkIcon,
  YouTube as YouTubeIcon,
  Article as ArticleIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import CoursePublicFeedbackPanel from './CoursePublicFeedbackPanel';

const materialTypeIcons = {
  youtube: YouTubeIcon,
  pdf: PdfIcon,
  document: ArticleIcon,
  wiki: ArticleIcon,
  medium: ArticleIcon,
  url: LinkIcon
};

const getYouTubeId = (url) => {
  if (!url) return null;
  const m = String(url).match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
  );
  return m ? m[1] : null;
};

const formatDuration = (minutes) => {
  if (!minutes) return '—';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const formatDateShort = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(d);
  }
};

const formatCourseSchedule = (c) => {
  const parts = [];
  if (c.start_date || c.end_date) {
    parts.push(`${formatDateShort(c.start_date) || '—'} → ${formatDateShort(c.end_date) || '—'}`);
  }
  if (c.schedule_model && c.schedule_model !== 'none') {
    parts.push(String(c.schedule_model).replace(/_/g, ' '));
  }
  if (c.weekdays && Array.isArray(c.weekdays) && c.weekdays.length) {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    parts.push(c.weekdays.map((n) => dayNames[n] || n).join(', '));
  }
  if (c.session_start_time || c.session_end_time) {
    parts.push(`${c.session_start_time || ''}–${c.session_end_time || ''}`.replace(/^–|–$/g, ''));
  }
  return parts.length ? parts.join(' · ') : '—';
};

const TrainingCatalogDetail = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => (searchParams.get('tab') === 'feedback' ? 5 : 0));
  const [preview, setPreview] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [actionCourseId, setActionCourseId] = useState(null);
  const [selectedTitleIds, setSelectedTitleIds] = useState([]);
  const [filterSpotId, setFilterSpotId] = useState(null);
  const [focusOutlineCourseId, setFocusOutlineCourseId] = useState(null);
  const [previewMaterial, setPreviewMaterial] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/training/catalog/category/${categoryId}`);
      setPreview(r.data.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'Failed to load program');
      navigate('/training/catalog');
    } finally {
      setLoading(false);
    }
  }, [categoryId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const flatCourses = useMemo(() => {
    if (!preview?.learningSpots) return [];
    const flat = [];
    preview.learningSpots.forEach((spot) => {
      (spot.courses || []).forEach((c) => {
        flat.push({ ...c, subCategoryName: spot.name, subCategoryId: spot.id });
      });
    });
    return flat;
  }, [preview]);

  const enrolledTitleSet = useMemo(() => {
    const ids = preview?.enrollment?.selectedTrainingTitleIds || [];
    return new Set(ids.map((id) => String(id)));
  }, [preview]);

  const isCourseEnrolled = useCallback(
    (courseId) => enrolledTitleSet.has(String(courseId)),
    [enrolledTitleSet]
  );

  useEffect(() => {
    if (flatCourses.length === 1) {
      setSelectedTitleIds([flatCourses[0].id]);
    } else {
      setSelectedTitleIds([]);
    }
  }, [flatCourses]);

  const handleEnroll = useCallback(async () => {
    if (!preview?.category) return;
    if (flatCourses.length === 0) {
      toast.error('There are no published courses to enroll in yet.');
      return;
    }
    if (flatCourses.length > 1 && selectedTitleIds.length === 0) {
      toast.error('Select one or more courses to enroll.');
      return;
    }
    setEnrolling(true);
    try {
      const payload = {
        category_id: preview.category.id,
        training_title_ids: flatCourses.length > 1 ? selectedTitleIds : [flatCourses[0].id]
      };
      await api.post('/training/user/subscribe', payload);
      toast.success(`Enrolled in ${preview.category.name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  }, [preview, flatCourses, selectedTitleIds, load]);

  const handleEnrollCourse = useCallback(
    async (courseId) => {
      if (!preview?.category || !courseId) return;
      setActionCourseId(courseId);
      try {
        if (preview.enrollment?.enrolled) {
          await api.post('/training/user/enrollment/add-courses', {
            category_id: preview.category.id,
            training_title_ids: [courseId]
          });
          toast.success('Course added to your enrollment');
        } else {
          await api.post('/training/user/subscribe', {
            category_id: preview.category.id,
            training_title_ids: [courseId]
          });
          toast.success('Enrolled in course');
        }
        await load();
      } catch (e) {
        toast.error(e.response?.data?.message || 'Failed to enroll in course');
      } finally {
        setActionCourseId(null);
      }
    },
    [preview, load]
  );

  const handleDropCourse = useCallback(
    async (courseId) => {
      if (!preview?.category || !courseId) return;
      if (!window.confirm('Drop this course from your enrollment? Your progress for this course will be removed.')) {
        return;
      }
      setActionCourseId(courseId);
      try {
        await api.post('/training/user/enrollment/drop-course', {
          category_id: preview.category.id,
          training_title_id: courseId
        });
        toast.success('Course dropped from enrollment');
        await load();
      } catch (e) {
        toast.error(e.response?.data?.message || 'Failed to drop course');
      } finally {
        setActionCourseId(null);
      }
    },
    [preview, load]
  );

  const handleReenrollCourse = useCallback(
    async (courseId) => {
      if (!preview?.category || !courseId) return;
      if (
        !window.confirm(
          'Reenroll in this course? Progress for this course will be reset and it counts toward your enrollment cycle limit.'
        )
      ) {
        return;
      }
      setActionCourseId(courseId);
      try {
        await api.post('/training/user/enrollment/reenroll-course', {
          category_id: preview.category.id,
          training_title_id: courseId
        });
        toast.success('Reenrolled in course');
        await load();
      } catch (e) {
        toast.error(e.response?.data?.message || 'Failed to reenroll');
      } finally {
        setActionCourseId(null);
      }
    },
    [preview, load]
  );

  const openCoursesForSpot = (spotId) => {
    setFilterSpotId(spotId || null);
    setTab(2);
  };

  const openOutlineForCourse = (courseId) => {
    setFocusOutlineCourseId(courseId);
    setFilterSpotId(null);
    setTab(3);
  };

  const renderCourseActions = (c) => {
    const learner = c.learner || {};
    const busy = actionCourseId === c.id;
    const enrollmentId = preview?.enrollment?.enrollmentId;
    return (
      <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        {learner.canLaunch && enrollmentId && (
          <Button
            size="small"
            variant="contained"
            startIcon={<LaunchIcon />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/training/learn/${enrollmentId}`);
            }}
          >
            Launch
          </Button>
        )}
        {learner.canEnroll && (
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              handleEnrollCourse(c.id);
            }}
          >
            {busy ? '…' : 'Enroll'}
          </Button>
        )}
        {learner.canDrop && (
          <Button
            size="small"
            color="warning"
            variant="outlined"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              handleDropCourse(c.id);
            }}
          >
            {busy ? '…' : 'Drop from Enrollment'}
          </Button>
        )}
        {learner.canReenroll && (
          <Button
            size="small"
            color="secondary"
            variant="outlined"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              handleReenrollCourse(c.id);
            }}
          >
            {busy ? '…' : 'Reenroll'}
          </Button>
        )}
        {!learner.canEnroll && !learner.enrolled && !learner.canReenroll && (
          <Typography variant="caption" color="text.secondary">
            No enrollments left
          </Typography>
        )}
      </Stack>
    );
  };

  if (loading || !preview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { category, learningSpots, enrollment } = preview;
  const enrolled = enrollment?.enrolled;
  const enrollmentId = enrollment?.enrollmentId;

  const coursesForTab = filterSpotId
    ? flatCourses.filter((c) => c.subCategoryId === filterSpotId)
    : flatCourses;
  const filterSpotName = filterSpotId
    ? learningSpots.find((s) => s.id === filterSpotId)?.name
    : null;

  const examOutlineRows = [];
  flatCourses.forEach((c) => {
    (c.outline || []).forEach((row, ri) => {
      if (String(row.item_type || '').toLowerCase() === 'exam') {
        examOutlineRows.push({
          key: `${c.id}-${row.order}-${ri}`,
          courseName: c.name,
          subCategoryName: c.subCategoryName,
          order: row.order,
          item_type: row.item_type,
          summary: row.summary,
          content_type: row.content_type
        });
      }
    });
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/training/catalog')} size="large" aria-label="Back to catalog">
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: category.color || '#6366f1', width: 40, height: 40 }}>
              <TrainingIcon />
            </Avatar>
            {category.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Enroll, launch, drop (when allowed), or reenroll by course. Click a course to open its outline.
          </Typography>
        </Box>
        {!enrolled && (
          <Button
            variant="contained"
            onClick={handleEnroll}
            disabled={enrolling || flatCourses.length === 0}
            sx={{ minWidth: 140 }}
          >
            {enrolling ? 'Enrolling…' : 'Enroll now'}
          </Button>
        )}
        {enrolled && (
          <>
            <Chip label={`Enrolled · ${enrollment.status || 'active'}`} color="primary" variant="outlined" />
            {enrollmentId && (
              <Button
                variant="contained"
                onClick={() => navigate(`/training/learn/${enrollmentId}`)}
                sx={{ minWidth: 140 }}
              >
                {enrollment.status === 'completed' ? 'Review' : 'Start attending'}
              </Button>
            )}
          </>
        )}
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Drop from Enrollment</strong> appears only when the instructor allows it and you are still inside the drop
        window. Completed courses cannot be dropped — use <strong>Reenroll</strong> if cycles remain.
      </Alert>

      {!enrolled && flatCourses.length > 1 && (
        <Card sx={{ mb: 2 }} variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Courses to include in your enrollment
            </Typography>
            <FormGroup>
              {flatCourses.map((c) => (
                <FormControlLabel
                  key={c.id}
                  control={
                    <Checkbox
                      checked={selectedTitleIds.includes(c.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTitleIds((prev) => [...prev, c.id]);
                        } else {
                          setSelectedTitleIds((prev) => prev.filter((id) => id !== c.id));
                        }
                      }}
                    />
                  }
                  label={`${c.name} · ${c.subCategoryName} · ${c.delivery_type === 'ILT' ? 'ILT' : 'SPT'}`}
                />
              ))}
            </FormGroup>
          </CardContent>
        </Card>
      )}

      {flatCourses.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          There are no published courses in this program yet.
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Chip icon={<CategoryIcon />} label={`${category.materialCount ?? 0} materials in library`} variant="outlined" />
          <Chip icon={<TimerIcon />} label={formatDuration(category.totalDuration)} variant="outlined" />
          <Chip icon={<CourseIcon />} label={`${category.publishedCourseCount ?? 0} published courses`} variant="outlined" color="primary" />
          <Chip icon={<SubcategoryIcon />} label={`${learningSpots.length} sub-categories`} variant="outlined" />
          {enrolled && (
            <Chip color="success" variant="outlined" label={`${enrolledTitleSet.size} course(s) enrolled`} />
          )}
        </CardContent>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => {
            setTab(v);
            if (v !== 2) setFilterSpotId(null);
            if (v !== 3) setFocusOutlineCourseId(null);
          }}
        >
          <Tab icon={<CategoryIcon fontSize="small" />} iconPosition="start" label="Category" />
          <Tab icon={<SubcategoryIcon fontSize="small" />} iconPosition="start" label="Sub-categories" />
          <Tab icon={<CourseIcon fontSize="small" />} iconPosition="start" label="Courses" />
          <Tab icon={<OutlineIcon fontSize="small" />} iconPosition="start" label="Course outlines" />
          <Tab icon={<ExamOutlineIcon fontSize="small" />} iconPosition="start" label="Exams" />
          <Tab label="Feedback" />
        </Tabs>
      </Box>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" onClick={() => setTab(1)} startIcon={<SubcategoryIcon />}>
              Go to Sub-Category
            </Button>
          </Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {category.description || 'No description provided for this category.'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <MaterialsLibraryIcon color="primary" />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Materials library ({category.libraryMaterials?.length ?? category.materialCount ?? 0})
              </Typography>
            </Stack>
            <Button
              component={MuiLink}
              href={`/training/materials?categoryId=${category.id}`}
              underline="hover"
              variant="outlined"
              size="small"
              startIcon={<MaterialsLibraryIcon />}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/training/materials?tab=materials&categoryId=${category.id}`);
              }}
            >
              Open Materials Library
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Reusable items in this program. Use <strong>Preview</strong> to view content here, or open the Materials Library to manage them.
          </Typography>
          {(category.libraryMaterials || []).length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {category.libraryMaterials.map((m) => {
                    const TypeIcon = materialTypeIcons[m.content_type] || LinkIcon;
                    return (
                      <TableRow key={m.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TypeIcon fontSize="small" color="action" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {m.title}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{m.content_type}</TableCell>
                        <TableCell>{m.duration_minutes ? `${m.duration_minutes} min` : '—'}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PreviewIcon />}
                            onClick={() => setPreviewMaterial(m)}
                          >
                            Preview
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No materials in this category library yet.{' '}
              <MuiLink
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate(`/training/materials?tab=materials&categoryId=${category.id}`)}
              >
                Go to Materials Library
              </MuiLink>
            </Typography>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Grid container spacing={2}>
          {learningSpots.map((spot) => {
            const courses = spot.courses || [];
            const completedCount = courses.filter((c) => !!c.learner?.completed).length;
            const enrolledCount = courses.filter(
              (c) => !!c.learner?.enrolled && !c.learner?.completed
            ).length;
            const notEnrolledCount = courses.length - completedCount - enrolledCount;
            return (
              <Grid item xs={12} md={6} key={spot.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardActionArea onClick={() => openCoursesForSpot(spot.id)} sx={{ height: '100%', alignItems: 'stretch' }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {spot.name}
                        </Typography>
                        <Chip size="small" color="primary" variant="outlined" label="View courses →" />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {spot.description || '—'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                        <Chip size="small" label={`${courses.length} published`} />
                        <Chip size="small" color="success" label={`${completedCount} completed`} />
                        <Chip size="small" color="primary" label={`${enrolledCount} enrolled`} />
                        <Chip size="small" variant="outlined" label={`${notEnrolledCount} not enrolled`} />
                      </Box>
                      {courses.map((c) => {
                        const courseCompleted = !!c.learner?.completed;
                        const courseEnrolled = courseCompleted || !!c.learner?.enrolled || isCourseEnrolled(c.id);
                        return (
                          <Box
                            key={c.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                              p: 1,
                              mb: 0.75,
                              borderRadius: 1,
                              bgcolor: courseCompleted
                                ? 'rgba(46, 125, 50, 0.12)'
                                : courseEnrolled
                                  ? 'rgba(46, 125, 50, 0.08)'
                                  : 'grey.50',
                              border: '1px solid',
                              borderColor: courseCompleted || courseEnrolled ? 'success.light' : 'divider'
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>
                              {c.name}
                              {c.edition_label ? ` (${c.edition_label})` : ''}
                            </Typography>
                            <Chip
                              size="small"
                              color={courseCompleted ? 'success' : courseEnrolled ? 'primary' : 'default'}
                              variant={courseCompleted || courseEnrolled ? 'filled' : 'outlined'}
                              label={
                                courseCompleted
                                  ? 'Completed'
                                  : courseEnrolled
                                    ? 'Already enrolled'
                                    : 'Not enrolled'
                              }
                            />
                          </Box>
                        );
                      })}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
          {learningSpots.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary">No sub-categories yet.</Typography>
            </Grid>
          )}
        </Grid>
      )}

      {tab === 2 && (
        <Box>
          {filterSpotId && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => setFilterSpotId(null)}>
                  Show all courses
                </Button>
              }
            >
              Showing courses in sub-category: <strong>{filterSpotName || '—'}</strong>
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Course</TableCell>
                  <TableCell>Sub-category</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coursesForTab.map((c) => {
                  const learner = c.learner || {};
                  const courseEnrolled = !!learner.enrolled;
                  return (
                    <TableRow
                      key={c.id}
                      hover
                      sx={{ cursor: 'pointer', bgcolor: courseEnrolled ? 'rgba(46, 125, 50, 0.04)' : undefined }}
                      onClick={() => openOutlineForCourse(c.id)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {c.name}
                          {c.edition_label ? ` (${c.edition_label})` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click for outline
                        </Typography>
                        {c.feedbackSummary?.count > 0 && (
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                            <Rating value={Number(c.feedbackSummary.avgRating) || 0} precision={0.1} readOnly size="small" />
                            <Typography variant="caption" color="text.secondary">
                              {c.feedbackSummary.avgRating} ({c.feedbackSummary.count})
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCoursesForSpot(c.subCategoryId);
                          }}
                        >
                          {c.subCategoryName}
                        </Button>
                      </TableCell>
                      <TableCell>{c.delivery_type === 'ILT' ? 'Instructor-led (ILT)' : 'Self-paced (SPT)'}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>{formatCourseSchedule(c)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Chip
                            size="small"
                            color={learner.completed ? 'success' : courseEnrolled ? 'primary' : 'default'}
                            variant={courseEnrolled ? 'filled' : 'outlined'}
                            label={
                              learner.completed
                                ? 'Completed'
                                : courseEnrolled
                                  ? 'Already enrolled'
                                  : 'Not enrolled'
                            }
                          />
                          {learner.canDrop && learner.dropDeadline && (
                            <Typography variant="caption" color="text.secondary">
                              Drop until {formatDateShort(learner.dropDeadline)}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {renderCourseActions(c)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {coursesForTab.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No published courses{filterSpotId ? ' in this sub-category' : ''}.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === 3 && (
        <Box>
          {flatCourses.map((c) => {
            const learner = c.learner || {};
            const isExpanded =
              focusOutlineCourseId != null
                ? String(c.id) === String(focusOutlineCourseId)
                : flatCourses.length <= 3;
            return (
              <Accordion
                key={c.id}
                expanded={isExpanded}
                onChange={(_, exp) => {
                  if (exp) setFocusOutlineCourseId(c.id);
                  else if (String(focusOutlineCourseId) === String(c.id)) setFocusOutlineCourseId(null);
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {c.name}
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({c.subCategoryName})
                      </Typography>
                    </Typography>
                    <Chip
                      size="small"
                      color={learner.completed ? 'success' : learner.enrolled ? 'primary' : 'default'}
                      variant={learner.enrolled ? 'filled' : 'outlined'}
                      label={
                        learner.completed
                          ? 'Completed'
                          : learner.enrolled
                            ? 'Already enrolled'
                            : 'Not enrolled'
                      }
                    />
                    <Box sx={{ ml: 'auto' }} onClick={(e) => e.stopPropagation()}>
                      {renderCourseActions(c)}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {learner.canDrop && learner.dropDeadline && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      Drop from Enrollment available until {formatDateShort(learner.dropDeadline)} (
                      {c.drop_window_days || 14} day window).
                    </Alert>
                  )}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={56}>#</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Summary</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(c.outline || []).map((row, ri) => (
                          <TableRow key={`${c.id}-${row.order}-${ri}`}>
                            <TableCell>{row.order}</TableCell>
                            <TableCell>
                              <Chip size="small" label={row.item_type || '—'} />
                              {row.content_type && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {row.content_type}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{row.summary || row.label || '—'}</TableCell>
                          </TableRow>
                        ))}
                        {(!c.outline || c.outline.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={3}>No outline rows yet.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
          {flatCourses.length === 0 && (
            <Typography color="text.secondary">No published courses to show outlines for.</Typography>
          )}
        </Box>
      )}

      {tab === 4 && (
        <Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Course</TableCell>
                  <TableCell>Sub-category</TableCell>
                  <TableCell width={56}>#</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Summary</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {examOutlineRows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>{row.courseName}</TableCell>
                    <TableCell>{row.subCategoryName}</TableCell>
                    <TableCell>{row.order}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.item_type || 'exam'} />
                    </TableCell>
                    <TableCell>{row.summary || '—'}</TableCell>
                  </TableRow>
                ))}
                {examOutlineRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No exam steps in published course outlines for this category.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === 5 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Approved customer reviews appear below. After you complete a course, you can submit your own feedback for
            instructor approval.
          </Alert>
          {flatCourses.length === 0 ? (
            <Typography color="text.secondary">No published courses in this program.</Typography>
          ) : (
            flatCourses.map((c) => (
              <Card key={c.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
                <CoursePublicFeedbackPanel trainingTitleId={c.id} courseName={c.name} />
              </Card>
            ))
          )}
        </Box>
      )}

      <Dialog
        open={!!previewMaterial}
        onClose={() => setPreviewMaterial(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {previewMaterial &&
            React.createElement(materialTypeIcons[previewMaterial.content_type] || LinkIcon, {
              fontSize: 'small',
              color: 'primary'
            })}
          {previewMaterial?.title || 'Material preview'}
        </DialogTitle>
        <DialogContent dividers>
          {previewMaterial && (
            <Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip size="small" label={previewMaterial.content_type || '—'} />
                {previewMaterial.duration_minutes != null && (
                  <Chip size="small" variant="outlined" label={`${previewMaterial.duration_minutes} min`} />
                )}
                {previewMaterial.difficulty_level && (
                  <Chip size="small" variant="outlined" label={previewMaterial.difficulty_level} />
                )}
              </Stack>
              {previewMaterial.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {previewMaterial.description}
                </Typography>
              )}
              {(() => {
                const url = previewMaterial.content_url || previewMaterial.file_path;
                const type = previewMaterial.content_type;
                if (type === 'youtube') {
                  const videoId = getYouTubeId(url);
                  if (videoId) {
                    return (
                      <Box sx={{ position: 'relative', paddingTop: '56.25%', width: '100%' }}>
                        <iframe
                          title={previewMaterial.title}
                          src={`https://www.youtube.com/embed/${videoId}`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius: 8
                          }}
                        />
                      </Box>
                    );
                  }
                }
                if ((type === 'pdf' || type === 'document') && url) {
                  return (
                    <Box sx={{ height: 480, width: '100%' }}>
                      <iframe
                        title={previewMaterial.title}
                        src={url}
                        style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
                      />
                    </Box>
                  );
                }
                if (url) {
                  return (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This material opens as an external link.
                      <Box sx={{ mt: 1.5 }}>
                        <Button
                          variant="contained"
                          startIcon={<OpenIcon />}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open material
                        </Button>
                      </Box>
                    </Alert>
                  );
                }
                return (
                  <Alert severity="warning">No preview URL is available for this material.</Alert>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {previewMaterial?.content_url || previewMaterial?.file_path ? (
            <Button
              startIcon={<OpenIcon />}
              href={previewMaterial.content_url || previewMaterial.file_path}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab
            </Button>
          ) : null}
          <Button onClick={() => setPreviewMaterial(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingCatalogDetail;
